/**
 * Passing by: the swipe cards (brief §9a).
 *
 * The Komoot motif. You walk past somewhere that already has something on the map and the app
 * asks, briefly, whether it still holds. It is deliberately **not** the confirmation from §9:
 * that acts on one fresh report inside a two-hour window, this acts on the *place*, at any
 * time, and has a correspondingly weaker effect.
 *
 * Everything here is pure, and everything about *when a card appears* is decided on the
 * device — the client already holds the hot-cell list for the geofence, so no extra request
 * and no extra position leaves.
 */

import { MIN_SIGHTINGS, VIBE_CUTOFF_MS } from './gdi.ts';
import type { Cell } from './h3.ts';
import { isNear } from './h3.ts';

const DAY = 24 * 60 * 60 * 1000;

/** Brief §9a.1 and §9a.4. Rules, not preferences. */
export const DWELL_SECONDS = 60;
export const REJUDGE_AFTER_MS = 7 * DAY;
export const CARD_CUTOFF_MS = 28 * DAY;
export const MAX_OPEN_CARDS = 10;
export const DAILY_SWIPE_LIMIT = 30;
export const NOTIFY_AFTER_SECONDS = 3 * 60;
export const PASSBY_RING = 1;
/** How long the last swipe can be taken back (brief §14). */
export const UNDO_WINDOW_MS = 5000;

/** Right, left, up, down. `skip` casts no vote; `rate` opens the report sheet instead. */
export const SWIPES = {
  good: 'good',
  lame: 'lame',
  rate: 'rate',
  skip: 'skip',
} as const;
export type Swipe = (typeof SWIPES)[keyof typeof SWIPES];

export function isSwipe(value: unknown): value is Swipe {
  return value === 'good' || value === 'lame' || value === 'rate' || value === 'skip';
}

/**
 * The vote a swipe casts, or `0` for the two that cast none.
 *
 * A right or left swipe creates **no report** and never enters `Σ contrib(s)`. That is the
 * point: otherwise the GDI would measure how many people walked past rather than how much was
 * reported. Only the up-swipe makes a real report, and it does so through the ordinary
 * reporting endpoint.
 */
export function voteFor(swipe: Swipe): 1 | -1 | 0 {
  if (swipe === SWIPES.good) return 1;
  if (swipe === SWIPES.lame) return -1;
  return 0;
}

/** What the client knows about a cell, from the feed it already holds for the geofence. */
export type CardCandidate = {
  cell: Cell;
  sightingCount: number;
  /** Epoch ms of the newest report in the cell. */
  lastSightingAt: number | null;
  /** True when every report in the cell is the viewer's own. */
  allMine: boolean;
};

export type DwellState = {
  /** The cell the device has been sitting in (or beside), and since when. */
  cell: Cell | null;
  since: number;
};

export type CardReason =
  | 'too_few_sightings'
  | 'all_mine'
  | 'too_old'
  | 'judged_recently'
  | 'not_here'
  | 'not_long_enough';

export type CardDecision = { ok: true } | { ok: false; reason: CardReason };

export type CardInput = {
  candidate: CardCandidate;
  dwell: DwellState;
  /** Epoch ms of the last time this viewer judged this cell, from local storage. */
  judgedAt: number | undefined;
  now: number;
};

/**
 * Should this cell produce a card right now?
 *
 * The dwell requirement is the important one. Without it a train ride through Stuttgart
 * collects forty cards, not one of which rests on anybody having looked at anything — and a
 * stack of forty cards is a stack nobody answers honestly.
 */
export function shouldOfferCard(input: CardInput): CardDecision {
  const { candidate, dwell, now } = input;

  if (candidate.sightingCount < MIN_SIGHTINGS) return { ok: false, reason: 'too_few_sightings' };
  // Judging a place only you have reported is judging yourself.
  if (candidate.allMine) return { ok: false, reason: 'all_mine' };
  if (candidate.lastSightingAt === null || now - candidate.lastSightingAt > CARD_CUTOFF_MS) {
    return { ok: false, reason: 'too_old' };
  }
  if (input.judgedAt !== undefined && now - input.judgedAt < REJUDGE_AFTER_MS) {
    return { ok: false, reason: 'judged_recently' };
  }
  if (dwell.cell === null || !isNear(dwell.cell, candidate.cell, PASSBY_RING)) {
    return { ok: false, reason: 'not_here' };
  }
  if ((now - dwell.since) / 1000 < DWELL_SECONDS) return { ok: false, reason: 'not_long_enough' };

  return { ok: true };
}

/**
 * Keep the dwell timer honest as the device moves.
 *
 * Staying inside the ring keeps the clock running — walking one hexagon over while waiting
 * for a friend outside a bar is still waiting outside that bar. Leaving it resets, because
 * the timer is meant to mean "you were there", not "you were around here somewhere today".
 */
export function updateDwell(state: DwellState, cell: Cell, now: number): DwellState {
  if (state.cell !== null && isNear(state.cell, cell, PASSBY_RING)) return state;
  return { cell, since: now };
}

/** The deck, newest first and capped — brief §9a.1's "at most 10, oldest fall out". */
export function trimDeck<T>(cards: readonly T[]): T[] {
  return cards.slice(0, MAX_OPEN_CARDS);
}

/** Whether the dwell has been long enough to be worth a local notification (brief §9a.1). */
export function worthNotifying(dwell: DwellState, now: number): boolean {
  return dwell.cell !== null && (now - dwell.since) / 1000 >= NOTIFY_AFTER_SECONDS;
}

export type PassbyRefusal = 'not_here' | 'no_sightings';

export const PASSBY_MESSAGES: Record<PassbyRefusal, string> = {
  not_here: 'Dafür musst du in der Nähe sein.',
  no_sightings: 'Hier ist nichts gemeldet, was man beurteilen könnte.',
};

/**
 * The server's half of the check (brief §9a.1).
 *
 * It verifies the two things it can: that there is something in the cell to judge, and that
 * the rounded cell in the request is near the rounded cell the person says they are standing
 * in. The **dwell time is a client assertion and is not verifiable** — the server has no
 * position history and is never going to have one. That is a known, accepted hole for a
 * project among friends; closing it would mean keeping exactly what §2 forbids.
 *
 * Note what is *not* checked here: the seven-day rule. §9a.1 says a card is not **offered**
 * again for seven days; §9a.4 says the vote itself stays **changeable** until it expires.
 * Those are consistent, and the difference lives one layer down — a second swipe inside the
 * window updates the existing vote rather than being refused or counted twice.
 */
export function canSwipe(input: {
  targetCell: Cell;
  viewerCell: Cell;
  sightingCount: number;
}): { ok: true } | { ok: false; reason: PassbyRefusal } {
  if (input.sightingCount < MIN_SIGHTINGS) return { ok: false, reason: 'no_sightings' };
  if (!isNear(input.viewerCell, input.targetCell, PASSBY_RING)) {
    return { ok: false, reason: 'not_here' };
  }
  return { ok: true };
}

/**
 * Schwarmgefühl: after a right or left swipe, how did the others judge this place?
 *
 * Amendment 12. It is the question a card naturally asks once you have answered it — did the
 * people before you see it the same way? — and it is asked about **people**, not vote rows.
 * `vibeFrom` counts rows, which is right for a mood one regular may legitimately shape week by
 * week, and wrong for "two of three saw it like you": that sentence is about three people. So
 * each other person counts once, by their latest opinion.
 *
 * "The others" are the vibe's own crowd minus you: the same 180-day horizon, and the same
 * exclusion of whoever filed the newest report (§9a.4). A verdict that leans on the author's
 * opinion of their own find is one person's opinion, counted twice.
 *
 * One other person is enough, and that is deliberately *not* `VIBE_MIN_VOTES`. The instance
 * this was built for had eight swipes from three people in five weeks and not one cell that
 * three people had judged — a threshold of three would have fired zero times in the whole
 * history of the app. The wording carries the difference instead: one person reads "Einer war
 * vor dir hier", never a percentage.
 */
export type CrowdVote = { userId: string; vote: 1 | -1; createdAt: Date };

/** How many other people have judged the place, and how many of them the way you did. */
export type Crowd = { others: number; same: number };

export function crowdOf(input: {
  votes: readonly CrowdVote[];
  viewerId: string;
  vote: 1 | -1;
  /** The author of the cell's newest report, whose vote does not count (§9a.4). */
  excludeUserId: string | undefined;
  now: Date;
}): Crowd {
  const latest = new Map<string, CrowdVote>();
  for (const entry of input.votes) {
    if (entry.userId === input.viewerId || entry.userId === input.excludeUserId) continue;
    if (input.now.getTime() - entry.createdAt.getTime() > VIBE_CUTOFF_MS) continue;
    const known = latest.get(entry.userId);
    if (known === undefined || entry.createdAt > known.createdAt) latest.set(entry.userId, entry);
  }
  let same = 0;
  for (const entry of latest.values()) if (entry.vote === input.vote) same += 1;
  return { others: latest.size, same };
}

export type CrowdVerdict = 'first' | 'agreed' | 'disagreed' | 'split';

/** A strict majority either way. An exact half is a split, and with nobody before you, `first`. */
export function verdictOf(crowd: Crowd): CrowdVerdict {
  if (crowd.others === 0) return 'first';
  if (crowd.same * 2 > crowd.others) return 'agreed';
  if (crowd.same * 2 < crowd.others) return 'disagreed';
  return 'split';
}

/**
 * The sentence the deck shows once a swipe can no longer be taken back.
 *
 * Counted, never a percentage — "67 %" of three people is a claim about a crowd that does not
 * exist. And agreement is marked ✓ rather than 👍, because 👍 already means "war gut" on this
 * very card: two people agreeing that a place is lame would otherwise read as a thumbs-up.
 */
export function describeCrowd(vote: 1 | -1, crowd: Crowd): string {
  // What the people who disagreed with you said, for the sentences that name it.
  const theirs = vote === 1 ? 'lame 👎' : 'gut 👍';
  const { others, same } = crowd;

  if (others === 0) return 'Vor dir hat hier noch niemand geurteilt — Pionierarbeit 🧭';
  if (others === 1) {
    return same === 1
      ? "Einer war vor dir hier — und sah's genauso ✓"
      : `Einer war vor dir hier — und fand's ${theirs}`;
  }
  if (others === 2) {
    if (same === 2) return "Beide vor dir sahen's genauso ✓";
    if (same === 0) return `Beide vor dir fanden's ${theirs}`;
    return "Von zweien vor dir sah's einer wie du ≈";
  }
  if (same === others) return `Alle ${others} vor dir sahen's genauso ✓`;
  if (same === 0) return `Keiner der ${others} vor dir sah's wie du — mutig.`;

  const verdict = verdictOf(crowd);
  const mark = verdict === 'agreed' ? ' ✓' : verdict === 'split' ? ' ≈' : '';
  return `${same} von ${others} vor dir ${same === 1 ? "sah's" : "sahen's"} wie du${mark}`;
}
